/**
 * A onda que a bolha desenha por cima do áudio (H16).
 *
 * ── Por que onda, e não barra de progresso ────────────────────────────────
 *
 * Porque é o que faz um áudio PARECER uma mensagem de voz. WhatsApp, Telegram,
 * Signal e Slack usam todos o mesmo desenho, e não por moda: uma barra lisa é
 * a mesma coisa para um vídeo, um download ou um carregamento, e não diz nada
 * sobre o que está ali dentro. A onda diz — dá para ver onde a pessoa falou,
 * onde parou, e se o áudio tem conteúdo ou é um engano de dois segundos.
 *
 * ── Só a onda real, e a honestidade é a regra ────────────────────────────
 *
 * `ondaReal` é MEDIÇÃO: os picos do áudio decodificado. Ela só é calculada
 * quando a pessoa toca — que é quando o arquivo ia ser baixado de qualquer
 * jeito. Analisar tudo ao abrir a conversa baixaria cada áudio do histórico
 * para desenhar enfeite.
 *
 * Havia uma segunda onda, provisória, derivada do id do anexo, para a bolha
 * não mostrar um retângulo vazio antes do toque. Saiu com o lote 4 do design
 * system: uma onda igual para todo áudio é enfeite, e o componente
 * `shared/ui/audio` mostra um trilho fino até saber — não desenhar o que não se
 * sabe. Quem desenha a onda, e em que grade, é o `audio.css` de lá; daqui
 * saem só os números, de 0 a 1.
 */

/*
 * Altura da onda e da menor barra, como o `audio.css` as desenha: servem só
 * para o piso da medição — nenhuma barra some; silêncio vira um ponto, e um
 * ponto ainda é informação.
 */
export const ALTURA_DA_ONDA = 26;
export const ALTURA_MINIMA_PX = 3;
export const BARRAS = 32;

const ALTURA_MINIMA = ALTURA_MINIMA_PX / ALTURA_DA_ONDA;

/**
 * O teto do que se decodifica.
 *
 * `decodeAudioData` descomprime para PCM em memória: um minuto de áudio vira
 * uns 10 MB. Um documento de 100 MB derrubaria a aba para desenhar um enfeite.
 * Acima disto, a onda provisória fica — e o áudio toca igual.
 */
const TETO_PARA_ANALISE = 8 * 1024 * 1024;

/**
 * Os picos reais do áudio, ou `null` quando não deu.
 *
 * Devolver `null` em vez de lançar é deliberado: nenhuma falha aqui pode
 * atrapalhar o que importa, que é o áudio tocar. Formato que o navegador não
 * decodifica, rede que caiu, arquivo grande demais — em todos, a onda
 * provisória continua e ninguém vê erro nenhum.
 */
export async function ondaReal(url: string): Promise<number[] | null> {
  try {
    const resposta = await fetch(url);
    if (!resposta.ok) return null;

    const bytes = await resposta.arrayBuffer();
    if (bytes.byteLength > TETO_PARA_ANALISE) return null;

    const Contexto =
      window.AudioContext ??
      (window as never as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Contexto) return null;

    const contexto = new Contexto();
    try {
      const audio = await contexto.decodeAudioData(bytes);
      return picos(audio.getChannelData(0));
    } finally {
      // Sem isto cada áudio aberto deixa um contexto de som vivo, e o navegador
      // tem um teto pequeno deles — o sexto áudio simplesmente não analisaria.
      void contexto.close();
    }
  } catch {
    return null;
  }
}

/**
 * O pico de cada balde, normalizado pelo maior.
 *
 * PICO, e não média: a média de um trecho de fala com pausas dá quase zero, e
 * a onda sairia plana. O pico é o que desenha a sílaba.
 *
 * A normalização é pelo maior pico do próprio áudio, e não por 1.0: gravação
 * de celular longe da boca tem tudo abaixo de 0.2, e sem normalizar a onda
 * seria uma linha reta — que é o mesmo que não ter onda.
 */
function picos(amostras: Float32Array): number[] {
  const porBalde = Math.floor(amostras.length / BARRAS) || 1;
  const brutos: number[] = [];
  let maior = 0;

  for (let i = 0; i < BARRAS; i += 1) {
    let pico = 0;
    const inicio = i * porBalde;
    const fim = Math.min(inicio + porBalde, amostras.length);
    for (let j = inicio; j < fim; j += 1) {
      const v = Math.abs(amostras[j] ?? 0);
      if (v > pico) pico = v;
    }
    brutos.push(pico);
    if (pico > maior) maior = pico;
  }

  if (maior === 0) return brutos.map(() => ALTURA_MINIMA);
  return brutos.map((p) => Math.max(ALTURA_MINIMA, p / maior));
}
